import { createContext, useContext, useRef, useEffect, useCallback, useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { NOTIF_UNREAD_QK } from "@/components/notifications/NotificationBell";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

// Search jobs hit the legacy `/api/jobs/:id` route which speaks the
// scrape-job vocabulary (itemsFound etc.). Brief jobs (Task #211) use
// the generic `/api/background-jobs/:id` endpoint that just exposes
// status / result / errorMessage. Discriminating on `kind` lets one
// poller serve both without forking the polling loop.
// Payload echoed back into POST /workspaces/:id/briefs for the retry
// affordance. Kept in the watched-job entry so a failed brief can be
// re-enqueued without making the briefs page re-derive it from form
// state (which may have been reset / modal closed by then).
export interface BriefRetryPayload {
  title: string;
  useBookmarked: boolean;
  collectionId?: number;
  itemIds?: number[];
}

export type WatchedJob =
  | {
      kind: "search";
      jobId: number;
      keyword: string;
      workspaceId: number;
    }
  | {
      kind: "brief";
      jobId: number;
      title: string;
      workspaceId: number;
      // Used by the persistent in-progress indicator (notification bell
      // and per-workspace strip) so the user sees which workspace the
      // job belongs to without an extra lookup.
      workspaceName?: string;
      retryPayload: BriefRetryPayload;
    };

export type ActiveBriefJob = Extract<WatchedJob, { kind: "brief" }>;

interface JobWatcherContextValue {
  watchJob: (job: WatchedJob) => void;
  // Subset of active brief jobs scoped to a single workspace so the
  // briefs page can render a persistent in-progress indicator without
  // seeing every workspace's queue.
  activeBriefJobsFor: (workspaceId: number) => ActiveBriefJob[];
  // All active brief jobs across all workspaces — used by the global
  // notification bell so the indicator persists when the user
  // navigates away from the briefs page during generation.
  allActiveBriefJobs: ActiveBriefJob[];
  // Fire-and-forget helper invoked from notification-bell Retry
  // buttons. Re-POSTs the original brief payload and (on success)
  // starts watching the new job so it shows up in the indicator.
  retryBrief: (job: { workspaceId: number; payload: BriefRetryPayload; workspaceName?: string }) => Promise<void>;
}

const JobWatcherContext = createContext<JobWatcherContextValue>({
  watchJob: () => {},
  activeBriefJobsFor: () => [],
  allActiveBriefJobs: [],
  retryBrief: async () => {},
});

export function useJobWatcher() {
  return useContext(JobWatcherContext);
}

export function useActiveBriefJobs(workspaceId: number): ActiveBriefJob[] {
  const { activeBriefJobsFor } = useContext(JobWatcherContext);
  return activeBriefJobsFor(workspaceId);
}

export function useAllActiveBriefJobs(): ActiveBriefJob[] {
  return useContext(JobWatcherContext).allActiveBriefJobs;
}

function SuccessToast({
  keyword,
  itemCount,
  onView,
  toastId,
}: {
  keyword: string;
  itemCount: number;
  onView: () => void;
  toastId: string | number;
}) {
  return (
    <div
      className="flex items-center gap-3 w-full"
      style={{
        background: "#16161e",
        border: "1px solid rgba(245,147,59,0.25)",
        borderLeft: "3px solid #f1580c",
        borderRadius: "10px",
        padding: "12px 14px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.5), 0 0 0 1px rgba(245,147,59,0.08)",
        minWidth: "300px",
      }}
    >
      <div
        className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full"
        style={{ background: "rgba(245,147,59,0.15)", border: "1px solid rgba(245,147,59,0.3)" }}
      >
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
          <path
            d="M11.5 3.5L6 10.5L3.5 7.5"
            stroke="#f1580c"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white leading-tight">
          <span style={{ color: "#f1580c" }}>"{keyword}"</span> finished
        </p>
        <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.45)" }}>
          {itemCount} item{itemCount !== 1 ? "s" : ""} ready to view
        </p>
      </div>

      <button
        onClick={() => {
          toast.dismiss(toastId);
          onView();
        }}
        className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg transition-opacity hover:opacity-90 active:opacity-75"
        style={{ background: "linear-gradient(90deg, #F5933B, #f1580c)", color: "#fff", letterSpacing: "0.01em" }}
      >
        View Results
      </button>
    </div>
  );
}

function BriefSuccessToast({
  title,
  readyLabel,
  subtitleLabel,
  openLabel,
  onView,
  toastId,
}: {
  title: string;
  readyLabel: string;
  subtitleLabel: string;
  openLabel: string;
  onView: () => void;
  toastId: string | number;
}) {
  return (
    <div
      className="flex items-center gap-3 w-full"
      style={{
        background: "#16161e",
        border: "1px solid rgba(245,147,59,0.25)",
        borderLeft: "3px solid #f1580c",
        borderRadius: "10px",
        padding: "12px 14px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.5), 0 0 0 1px rgba(245,147,59,0.08)",
        minWidth: "300px",
      }}
    >
      <div
        className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full"
        style={{ background: "rgba(245,147,59,0.15)", border: "1px solid rgba(245,147,59,0.3)" }}
      >
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
          <path d="M11.5 3.5L6 10.5L3.5 7.5" stroke="#f1580c" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white leading-tight">
          <span style={{ color: "#f1580c" }}>"{title}"</span> {readyLabel}
        </p>
        <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.45)" }}>
          {subtitleLabel}
        </p>
      </div>
      <button
        onClick={() => {
          toast.dismiss(toastId);
          onView();
        }}
        className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg transition-opacity hover:opacity-90 active:opacity-75"
        style={{ background: "linear-gradient(90deg, #F5933B, #f1580c)", color: "#fff", letterSpacing: "0.01em" }}
      >
        {openLabel}
      </button>
    </div>
  );
}

function FailedToast({
  keyword,
  message,
  onRetry,
  retryLabel,
  toastId,
}: {
  keyword: string;
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
  toastId?: string | number;
}) {
  return (
    <div
      className="flex items-center gap-3 w-full"
      style={{
        background: "#16161e",
        border: "1px solid rgba(239,68,68,0.25)",
        borderLeft: "3px solid #ef4444",
        borderRadius: "10px",
        padding: "12px 14px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
        minWidth: "300px",
      }}
    >
      <div
        className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full"
        style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)" }}
      >
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
          <path
            d="M4.5 4.5L10.5 10.5M10.5 4.5L4.5 10.5"
            stroke="#ef4444"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white leading-tight">
          <span style={{ color: "#ef4444" }}>"{keyword}"</span> failed
        </p>
        <p className="text-xs mt-0.5 truncate" style={{ color: "rgba(255,255,255,0.45)" }}>
          {message}
        </p>
      </div>

      {onRetry && (
        <button
          onClick={() => {
            if (toastId !== undefined) toast.dismiss(toastId);
            onRetry();
          }}
          className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg transition-opacity hover:opacity-90 active:opacity-75"
          style={{
            background: "rgba(239,68,68,0.15)",
            border: "1px solid rgba(239,68,68,0.4)",
            color: "#ef4444",
            letterSpacing: "0.01em",
          }}
        >
          {retryLabel ?? "Retry"}
        </button>
      )}
    </div>
  );
}

export function JobWatcherProvider({ children }: { children: React.ReactNode }) {
  // Polling internals stay in a ref so the 4s interval doesn't have to
  // be torn down/rebuilt every time a job is added or removed. We
  // mirror the brief-job subset into React state (`activeBriefJobs`)
  // so consumer components (e.g. the briefs page) can render a
  // persistent in-progress indicator that updates reactively.
  const watchedJobs = useRef<Map<string, WatchedJob>>(new Map());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { t } = useTranslation("app");
  const [activeBriefJobs, setActiveBriefJobs] = useState<ActiveBriefJob[]>([]);

  const removeBriefFromState = useCallback((jobId: number) => {
    setActiveBriefJobs((prev) => prev.filter((j) => j.jobId !== jobId));
  }, []);

  const retryBrief = useCallback(async (input: {
    workspaceId: number;
    payload: BriefRetryPayload;
    workspaceName?: string;
  }) => {
    try {
      const res = await fetch(`${BASE}/api/workspaces/${input.workspaceId}/briefs`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input.payload),
      });
      if (!res.ok) {
        toast.error(t("briefs.retryFailed"));
        return;
      }
      const data = (await res.json()) as { jobId: number; title: string };
      const next: ActiveBriefJob = {
        kind: "brief",
        jobId: data.jobId,
        title: data.title || input.payload.title,
        workspaceId: input.workspaceId,
        workspaceName: input.workspaceName,
        retryPayload: input.payload,
      };
      watchedJobs.current.set(`brief:${next.jobId}`, next);
      setActiveBriefJobs((prev) =>
        prev.some((p) => p.jobId === next.jobId) ? prev : [...prev, next],
      );
      toast.success(t("briefs.queued"));
    } catch {
      toast.error(t("briefs.retryFailed"));
    }
  }, [t]);

  const enqueueBriefRetry = useCallback(
    (job: ActiveBriefJob) => retryBrief({
      workspaceId: job.workspaceId,
      payload: job.retryPayload,
      workspaceName: job.workspaceName,
    }),
    [retryBrief],
  );

  const watchJob = useCallback((job: WatchedJob) => {
    // Namespace the key by kind so a search job and brief job sharing
    // the same numeric id never overwrite each other in the polling map.
    watchedJobs.current.set(`${job.kind}:${job.jobId}`, job);
    if (job.kind === "brief") {
      setActiveBriefJobs((prev) => {
        if (prev.some((p) => p.jobId === job.jobId)) return prev;
        return [...prev, job];
      });
    }
  }, []);

  const activeBriefJobsFor = useCallback(
    (workspaceId: number) => activeBriefJobs.filter((j) => j.workspaceId === workspaceId),
    [activeBriefJobs],
  );

  useEffect(() => {
    async function checkJobs() {
      if (watchedJobs.current.size === 0) return;

      const jobsToCheck = Array.from(watchedJobs.current.entries());

      await Promise.all(
        jobsToCheck.map(async ([key, job]) => {
          try {
            const url =
              job.kind === "search"
                ? `${BASE}/api/jobs/${job.jobId}`
                : `${BASE}/api/background-jobs/${job.jobId}`;
            const res = await fetch(url, { credentials: "include" });
            if (!res.ok) {
              // Stop watching if the job is gone (deleted/expired) or
              // the session no longer has access — otherwise the
              // in-progress indicator would stay forever.
              if (res.status === 404 || res.status === 401 || res.status === 403) {
                watchedJobs.current.delete(key);
                if (job.kind === "brief") removeBriefFromState(job.jobId);
              }
              return;
            }
            const data = await res.json();

            if (data.status === "completed") {
              watchedJobs.current.delete(key);
              if (job.kind === "brief") removeBriefFromState(job.jobId);
              // Backend emitted a notification when the job completed —
              // refresh the bell badge so it appears without a 30s delay.
              queryClient.invalidateQueries({ queryKey: NOTIF_UNREAD_QK });

              if (job.kind === "search") {
                const itemCount = data.itemsFound ?? 0;
                toast.custom(
                  (id) => (
                    <SuccessToast
                      keyword={job.keyword}
                      itemCount={itemCount}
                      toastId={id}
                      onView={() =>
                        setLocation(
                          `/workspaces/${job.workspaceId}/results/${job.jobId}`
                        )
                      }
                    />
                  ),
                  { duration: 8000 }
                );
              } else {
                // Brief jobs return their result as a JSON string in
                // `result` so the toast can deep-link to the new brief
                // without a follow-up fetch.
                let briefId: number | null = null;
                try {
                  const parsed = data.result ? JSON.parse(data.result) : null;
                  if (parsed && typeof parsed.briefId === "number") briefId = parsed.briefId;
                } catch { /* result not JSON — fall back to list view */ }
                queryClient.invalidateQueries({
                  queryKey: [`/api/workspaces/${job.workspaceId}/briefs`],
                });
                // Notify the briefs page to auto-select the new brief
                // when the user is already viewing it (so they don't
                // have to click the toast). Page-mount URL parsing
                // only handles the initial-load case.
                if (briefId !== null && typeof window !== "undefined") {
                  window.dispatchEvent(
                    new CustomEvent("briefengine:brief-ready", {
                      detail: { workspaceId: job.workspaceId, briefId },
                    }),
                  );
                }
                toast.custom(
                  (id) => (
                    <BriefSuccessToast
                      title={job.title}
                      readyLabel={t("briefs.briefReadyTitle")}
                      subtitleLabel={t("briefs.briefReadySubtitle")}
                      openLabel={t("briefs.briefReadyOpen")}
                      toastId={id}
                      onView={() =>
                        setLocation(
                          briefId !== null
                            ? `/workspaces/${job.workspaceId}/briefs?brief=${briefId}`
                            : `/workspaces/${job.workspaceId}/briefs`
                        )
                      }
                    />
                  ),
                  { duration: 8000 }
                );
              }
            } else if (data.status === "failed") {
              watchedJobs.current.delete(key);
              if (job.kind === "brief") removeBriefFromState(job.jobId);
              queryClient.invalidateQueries({ queryKey: NOTIF_UNREAD_QK });

              const fallback =
                job.kind === "search"
                  ? "Something went wrong during the search."
                  : t("briefs.briefFailedFallback");
              const label = job.kind === "search" ? job.keyword : job.title;
              const briefJob = job.kind === "brief" ? job : null;
              toast.custom(
                (id) => (
                  <FailedToast
                    keyword={label}
                    message={data.errorMessage || fallback}
                    toastId={id}
                    retryLabel={briefJob ? t("briefs.retryButton") : undefined}
                    onRetry={briefJob ? () => enqueueBriefRetry(briefJob) : undefined}
                  />
                ),
                { duration: briefJob ? 12000 : 6000 }
              );
            }
          } catch {
          }
        })
      );
    }

    intervalRef.current = setInterval(checkJobs, 4000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [setLocation, queryClient, t, removeBriefFromState, enqueueBriefRetry]);

  return (
    <JobWatcherContext.Provider value={{ watchJob, activeBriefJobsFor, allActiveBriefJobs: activeBriefJobs, retryBrief }}>
      {children}
    </JobWatcherContext.Provider>
  );
}
